// JARVIS Voice Service — TTS (speak) + STT (listen) + Mute control

const MUTE_KEY = 'jarvis_voice_muted';
const VOICE_LANG = 'en-IN'; // Indian English

// ── TTS ──────────────────────────────────────────────────────────────────────
export const isMuted = () => localStorage.getItem(MUTE_KEY) === 'true';

export const setMuted = (val) => {
  localStorage.setItem(MUTE_KEY, val ? 'true' : 'false');
  if (val) window.speechSynthesis?.cancel();
};

export const toggleMute = () => {
  const next = !isMuted();
  setMuted(next);
  return next;
};

export const speak = (text, { priority = false, rate = 0.95, pitch = 1.0 } = {}) => {
  if (!window.speechSynthesis || isMuted() || !text) return;
  if (priority) window.speechSynthesis.cancel();

  const clean = text
    .replace(/\*\*/g, '').replace(/#{1,3} /g, '').replace(/`/g, '')
    .replace(/\n+/g, '. ').slice(0, 500);

  const utt = new SpeechSynthesisUtterance(clean);
  utt.lang  = VOICE_LANG;
  utt.rate  = rate;
  utt.pitch = pitch;

  // Pick best available voice
  const voices = window.speechSynthesis.getVoices();
  const preferred = voices.find(v =>
    v.lang.startsWith('en') && (v.name.includes('Google') || v.name.includes('Microsoft'))
  ) || voices.find(v => v.lang.startsWith('en'));
  if (preferred) utt.voice = preferred;

  window.speechSynthesis.speak(utt);
};

// Time-based greeting
export const greet = () => {
  const h = new Date().getHours();
  const g = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  speak(`${g} Sir. JARVIS is online and monitoring markets.`, { priority: true });
};

// Analysis result speak (short version)
export const speakAnalysis = (analysis) => {
  if (!analysis) return;
  const lines = analysis.split('\n').filter(Boolean).slice(0, 3).join('. ');
  speak(`Sir, ${lines}`, { rate: 0.9 });
};

// Level alert
export const speakAlert = (msg) => speak(`Sir, ${msg}`, { priority: true, rate: 0.88 });

// ── STT (Speech Recognition) ──────────────────────────────────────────────────
// All three places in the app that use SpeechRecognition (here, ChatInput.jsx,
// BottomBar.jsx) were swallowing recognition.onerror silently — the mic
// button would just quietly stop "listening" with zero indication of WHY
// (permission denied vs no mic found vs no speech heard vs network issue),
// which is exactly what makes "mic doesn't work" impossible to diagnose
// from the user side. This maps the Web Speech API's error codes to an
// actual explanation, shared across all three call sites.
export const describeVoiceError = (errorCode) => {
  switch (errorCode) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone permission नाकारलं आहे. Browser/App settings मध्ये जाऊन JARVIS ला microphone access द्या.';
    case 'audio-capture':
      return 'Microphone सापडला नाही. Device वर microphone जोडलेला/चालू आहे का बघा.';
    case 'network':
      return 'Voice recognition साठी internet connection लागतं — ते सध्या उपलब्ध नाही.';
    case 'no-speech':
      return null; // not a real error — user just didn't say anything, safe to ignore silently
    case 'aborted':
      return null; // user or app cancelled it deliberately
    default:
      return `Voice input मध्ये अडचण आली (${errorCode || 'unknown'}). परत प्रयत्न करा.`;
  }
};

let recognition = null;
let _onResult = null;
let _listening = false;

export const isListening = () => _listening;

export const startListening = (onResult, onError) => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) {
    console.warn('SpeechRecognition not supported');
    if (onError) onError('Ha browser voice input support करत नाही. Chrome किंवा Edge वापरा.');
    return false;
  }

  if (recognition) recognition.abort();

  recognition = new SR();
  recognition.lang = 'en-IN';
  recognition.continuous = false;
  recognition.interimResults = false;
  _onResult = onResult;
  _listening = true;

  recognition.onresult = (e) => {
    const transcript = e.results[0][0].transcript.trim();
    _listening = false;
    if (_onResult) _onResult(transcript);
  };

  recognition.onerror = (e) => {
    _listening = false;
    const msg = describeVoiceError(e.error);
    if (msg && onError) onError(msg);
  };
  recognition.onend  = () => { _listening = false; };

  recognition.start();
  return true;
};

export const stopListening = () => {
  if (recognition) { recognition.abort(); recognition = null; }
  _listening = false;
};

// Command detection — "JARVIS save rule", "save this", etc.
export const detectSaveCommand = (text = '') => {
  const t = text.toLowerCase();
  return t.includes('save rule') || t.includes('save this') ||
         t.includes('jarvis save') || t.includes('save level') ||
         t.includes('save pattern') || t.includes('save logic') ||
         t.includes('save kar') || t.includes('he save') ||
         t.includes('memory madhe') || t.includes('jarvis la save') ||
         t.includes('save this logic') || t.includes('save this rule');
};
